import { Box, Card, Stack, Typography } from '@mui/material';
import { isNil, lowerCase } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

import { DeploymentConfig } from '@/api/devices';
import { useLeaderboard } from '@/api/leaderboard/useLeaderboard';
import { useFleetSpottersLatestData } from '@/api/oceansApi/useFleetSpottersLatestData';
import { ConvertibleUnitPreferenceTypes, useDisplayPreferences } from '@/api/userAccount';
import { convert } from '@/common/convert';
import { Datum, DatumComponentParams, DatumUnit, normalizeQuantityType, unitInContext } from '@/components/data/Datum';
import { Timestamp } from '@/components/data/Timestamp';
import { SpotterContextProvider } from '@/context/SpotterContext';
import { OverallStatEntry, useTopSpotTourContext } from '@/context/TopSpotTourContext';
import { boldWeight, minorTextGrey } from '@/theme';

type ScrollEdge = 'top' | 'bottom' | 'both' | 'none';

const FADE_PX = 48;

const maskImage: Record<ScrollEdge, string> = {
    none: 'none',
    top: `linear-gradient(to bottom, black 0%, black calc(100% - ${FADE_PX}px), transparent 100%)`,
    bottom: `linear-gradient(to bottom, transparent 0%, black ${FADE_PX}px, black 100%)`,
    both: `linear-gradient(to bottom, transparent 0%, black ${FADE_PX}px, black calc(100% - ${FADE_PX}px), transparent 100%)`,
};

const LOOP_COPIES = 3;
const AUTO_SCROLL_GUARD_MS = 800;

export const TopSpotList = () => {
    const { entries, focusedEntry, selectEntry } = useTopSpotTourContext();
    const scrollRef = useRef<HTMLDivElement>(null);
    const focusedCardRefs = useRef<(HTMLElement | null)[]>(Array(LOOP_COPIES).fill(null));
    const isAutoScrollingRef = useRef(false);
    const autoScrollGuardRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loopedEntries = useMemo(
        () =>
            entries.length === 0
                ? []
                : Array.from({ length: LOOP_COPIES }).flatMap((_, copy) =>
                      entries.map((entry, index) => ({ entry, index, copy }))
                  ),
        [entries]
    );

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;
        const cards = focusedCardRefs.current.filter((c): c is HTMLElement => !!c);
        if (cards.length === 0) return;
        const viewportCenter = container.scrollTop + container.clientHeight / 2;
        const target = cards.reduce((best, card) =>
            Math.abs(card.offsetTop + card.offsetHeight / 2 - viewportCenter) <
            Math.abs(best.offsetTop + best.offsetHeight / 2 - viewportCenter)
                ? card
                : best
        );
        const scrollTop = target.offsetTop - container.clientHeight / 2 + target.offsetHeight / 2;
        isAutoScrollingRef.current = true;
        container.scrollTo({ top: scrollTop, behavior: 'smooth' });
        if (autoScrollGuardRef.current) clearTimeout(autoScrollGuardRef.current);
        autoScrollGuardRef.current = setTimeout(() => {
            isAutoScrollingRef.current = false;
            onScroll();
        }, AUTO_SCROLL_GUARD_MS);
    }, [focusedEntry]);

    const onScroll = () => {
        // Snap back to the middle copy if scrolled deep into the wraparound buffers (first or third copy).
        // Disabled during programmatic smooth scrolls so we don't disrupt the animation.
        if (isAutoScrollingRef.current) return;
        const el = scrollRef.current;
        if (!el) return;
        const sectionHeight = el.scrollHeight / LOOP_COPIES;
        if (el.scrollTop < sectionHeight * 0.5) {
            el.scrollTop += sectionHeight;
        } else if (el.scrollTop > sectionHeight * (LOOP_COPIES - 0.5)) {
            el.scrollTop -= sectionHeight;
        }
    };

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                maxHeight: 'calc(100vh - 100px)',
                zIndex: 10,
                overflowY: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                maskImage: maskImage.both,
                WebkitMaskImage: maskImage.both,
                p: '2px',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
            ref={scrollRef}
            onScroll={onScroll}
        >
            {loopedEntries.map(({ entry, index, copy }) => {
                const isFocused = entry === focusedEntry;
                const cardRef = isFocused
                    ? (el: HTMLElement | null) => {
                          focusedCardRefs.current[copy] = el;
                      }
                    : undefined;
                const onClick = () => selectEntry(index);
                return entry.stat === 'overall' ? (
                    <TopSpotOverallCard
                        key={`${copy}-${entry.title}`}
                        entry={entry}
                        isFocused={isFocused}
                        cardRef={cardRef}
                        onClick={onClick}
                    />
                ) : (
                    <TopSpotCard
                        key={`${copy}-${entry.name}`}
                        entry={entry}
                        isFocused={isFocused}
                        cardRef={cardRef}
                        onClick={onClick}
                    />
                );
            })}
        </Box>
    );
};

type TopSpotCardProps = {
    entry: ReturnType<typeof useLeaderboard>[number];
    isFocused: boolean;
    cardRef?: (el: HTMLElement | null) => void;
    onClick: () => void;
};

const TopSpotCard = ({ entry, isFocused, cardRef, onClick }: TopSpotCardProps) => {
    const { stat, variable, subtitle } = entry;

    const { spotterId, units, unitType } = useMemo(() => {
        if (stat === 'delta') {
            return { ...entry.max, ...entry };
        }
        return entry;
    }, [entry, stat]);

    const { data: spotterLatestData } = useFleetSpottersLatestData({ spotters: [spotterId], useCache: true });
    const datumContext = { deploymentConfig: { waterType: 'Saltwater' } as DeploymentConfig };

    const { name, owner, org } = spotterLatestData?.data[0] ?? {};

    const ownerName = org || owner;

    const variableName = useMemo(() => {
        if (variable === 'sensor:tilt') {
            return 'current meter tilt';
        }
        return 'sensorType' in entry ? lowerCase(entry.sensorType) : lowerCase(variable);
    }, [entry, variable]);

    const awardDetail = useMemo(() => {
        if (subtitle) {
            return subtitle;
        }
        switch (stat) {
            case 'max':
                return `Highest ${variableName}`;
            case 'min':
                return `Lowest ${variableName}`;
            case 'delta':
                return `Widest-ranging ${variableName}`;
        }
    }, [variableName, stat, subtitle]);

    return (
        <SpotterContextProvider spotterId={spotterId}>
            <Card
                ref={cardRef}
                variant="elevation"
                onClick={onClick}
                className={isFocused ? 'focused' : undefined}
                elevation={2}
                sx={{
                    boxSizing: 'border-box',
                    border: 0,
                    width: '220px',
                    '&.focused': { width: '400px', fontSize: '1.75rem' },
                    cursor: 'pointer',
                    transition: '400ms',
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        '.focused &': {
                            px: 3,
                            py: 3,
                        },
                        backgroundColor: '#0d284e',
                        color: 'white',
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '0.75em',
                                fontWeight: boldWeight,
                                transition: '400ms',
                            }}
                        >
                            {entry.name}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '0.5em',
                                transition: '400ms',
                                display: 'none',
                                opacity: 0,
                                '.focused &': {
                                    display: 'initial',
                                    opacity: 1,
                                },
                            }}
                        >
                            {awardDetail}
                        </Typography>
                    </Box>
                    <Box
                        component="img"
                        src={entry.ribbonSvg}
                        sx={{
                            height: '3em',
                            m: -2,
                            display: 'none',
                            '.focused &': { display: 'block' },
                        }}
                    />
                </Box>
                <Stack
                    sx={{
                        px: 2,
                        py: 1,
                        gap: 0.5,
                        '.focused &': {
                            px: 3,
                            py: 3,
                            gap: 1,
                        },
                    }}
                >
                    {entry.stat === 'delta' && (
                        <>
                            <Box sx={{ display: 'none', '.focused &': { display: 'initial' } }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        flexWrap: 'wrap',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Datum
                                        sx={{ fontSize: '1.25em', fontWeight: boldWeight }}
                                        value={entry.max.value}
                                        sourceUnit={units}
                                        type={unitType}
                                        context={datumContext}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '0.5em',
                                            color: minorTextGrey,
                                            display: 'none',
                                            opacity: 0,
                                            '.focused &': {
                                                display: 'initial',
                                                opacity: 1,
                                            },
                                            transition: '400ms',
                                        }}
                                    >
                                        <strong>Max </strong>
                                        <Timestamp
                                            variant="inherit"
                                            fallback=""
                                            value={entry.max.sample?.timestamp}
                                            type="distanceToNow"
                                        />
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        flexWrap: 'wrap',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Datum
                                        sx={{ fontSize: '1.25em', fontWeight: boldWeight }}
                                        value={entry.min.value}
                                        sourceUnit={units}
                                        type={unitType}
                                        context={datumContext}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '0.5em',
                                            color: minorTextGrey,
                                            display: 'none',
                                            opacity: 0,
                                            '.focused &': {
                                                display: 'initial',
                                                opacity: 1,
                                            },
                                            transition: '400ms',
                                        }}
                                    >
                                        <strong>Min </strong>
                                        <Timestamp
                                            variant="inherit"
                                            fallback=""
                                            value={entry.min.sample?.timestamp}
                                            type="distanceToNow"
                                        />
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ '.focused &': { display: 'none' } }}>
                                <DeltaDatum
                                    sx={{ fontSize: '1.25em', fontWeight: boldWeight }}
                                    values={[entry.max.value, entry.min.value]}
                                    sourceUnit={units}
                                    type={unitType}
                                    context={datumContext}
                                />
                            </Box>
                        </>
                    )}
                    {stat !== 'delta' && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                flexWrap: 'wrap',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Datum
                                sx={{ fontSize: '1.25em', fontWeight: boldWeight, whiteSpace: 'break-spaces' }}
                                value={entry.value}
                                sourceUnit={units}
                                type={unitType}
                                context={datumContext}
                            />
                            <Timestamp
                                sx={{
                                    fontSize: '0.5em',
                                    color: minorTextGrey,
                                    display: 'none',
                                    opacity: 0,
                                    '.focused &': {
                                        display: 'initial',
                                        opacity: 1,
                                    },
                                    transition: '400ms',
                                }}
                                value={entry.sample?.timestamp}
                                fallback=""
                                type="distanceToNow"
                            />
                        </Box>
                    )}
                    <Typography variant="body1" sx={{ fontSize: '0.75em', fontWeight: boldWeight }}>
                        {name || spotterId}
                        {isFocused && name && !name.includes(spotterId) && (
                            <span style={{ color: minorTextGrey }}> {spotterId}</span>
                        )}
                    </Typography>
                    {ownerName && (
                        <Typography
                            sx={{
                                color: minorTextGrey,
                                fontSize: '0.5em',
                                transition: '400ms',
                                display: 'none',
                                opacity: 0,
                                '.focused &': {
                                    display: 'initial',
                                    opacity: 1,
                                },
                            }}
                        >
                            {ownerName}
                        </Typography>
                    )}
                </Stack>
            </Card>
        </SpotterContextProvider>
    );
};

/**
 * If we're trying to render a unit-converted difference between two values, we need to convert the
 * values first before taking the difference, to account for offset conversions like temperature or
 * depth-from-pressure.
 */
const DeltaDatum = ({
    values,
    ...datumProps
}: Omit<DatumComponentParams, 'value'> & { values: [number | null | undefined, number | null | undefined] }) => {
    let { sourceUnit, targetUnit } = datumProps;
    const { type, context } = datumProps;
    const { data: unitPreferences } = useDisplayPreferences();
    const quantityType = normalizeQuantityType(type);

    if (isNil(values[0]) || isNil(values[1])) {
        return <Datum value={null} {...(datumProps as DatumComponentParams)} />;
    }

    if (type === 'unitless' || sourceUnit === undefined) {
        return <Datum value={Math.abs(values[0] - values[1])} {...(datumProps as DatumComponentParams)} />;
    }

    if (!targetUnit && unitPreferences && quantityType in unitPreferences) {
        targetUnit = unitPreferences[quantityType as ConvertibleUnitPreferenceTypes];
    }
    sourceUnit = unitInContext(quantityType, sourceUnit, context) ?? sourceUnit;
    targetUnit = unitInContext(quantityType, targetUnit, context);

    let convertedValues: [number, number];
    let renderedUnit: DatumUnit;
    if (sourceUnit && targetUnit && targetUnit !== sourceUnit) {
        try {
            convertedValues = [
                convert(values[0]).from(sourceUnit).to(targetUnit),
                convert(values[1]).from(sourceUnit).to(targetUnit),
            ];
            renderedUnit = targetUnit;
        } catch (err) {
            console.error(`Error converting ${sourceUnit} to ${targetUnit}`, err);
            convertedValues = values as [number, number];
            renderedUnit = sourceUnit;
        }
    } else {
        convertedValues = values as [number, number];
        renderedUnit = sourceUnit;
    }

    const delta = Math.abs(convertedValues[0] - convertedValues[1]);
    return <Datum value={delta} {...datumProps} type={type} sourceUnit={renderedUnit} targetUnit={renderedUnit} />;
};

type TopSpotOverallCardProps = {
    entry: OverallStatEntry;
    isFocused: boolean;
    cardRef?: (el: HTMLElement | null) => void;
    onClick: () => void;
};

const TopSpotOverallCard = ({
    entry: { title, subtitle, body, icons },
    isFocused,
    cardRef,
    onClick,
}: TopSpotOverallCardProps) => {
    return (
        <Card
            ref={cardRef}
            variant="elevation"
            onClick={onClick}
            className={isFocused ? 'focused' : undefined}
            elevation={2}
            sx={{
                boxSizing: 'border-box',
                border: 0,
                width: '220px',
                '&.focused': { width: '400px', fontSize: '1.75rem' },
                cursor: 'pointer',
                transition: '400ms',
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '.focused &': {
                        px: 3,
                        py: 3,
                    },
                    backgroundColor: '#2C40D7',
                    color: 'white',
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: '0.75em',
                            fontWeight: boldWeight,
                            transition: '400ms',
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.5em',
                            transition: '400ms',
                            display: 'none',
                            opacity: 0,
                            '.focused &': {
                                display: 'initial',
                                opacity: 1,
                            },
                        }}
                    >
                        {subtitle}
                    </Typography>
                </Box>
                <Stack direction="row" sx={{ mr: 2 }}>
                    {icons.map((icon, idx) => (
                        <Box
                            key={icon}
                            component="img"
                            src={icon}
                            sx={{
                                height: '2em',
                                m: -2,
                                pt: idx,
                                display: 'none',
                                '.focused &': { display: 'block' },
                            }}
                        />
                    ))}
                </Stack>
            </Box>
            <Stack
                sx={{
                    px: 2,
                    py: 1,
                    gap: 0.5,
                    '.focused &': {
                        px: 3,
                        py: 3,
                        gap: 1,
                    },
                }}
            >
                {body}
            </Stack>
        </Card>
    );
};
